import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {useAIStateContext, SendMessageParams} from "../../../contexts/ai_chat_state_context";
import {JobType} from "../../../schemas";
import ChatInput from "../ChatInput/normal_text";
import {FiFileText, FiLock, FiMessageSquare} from "react-icons/fi";
import {
    ChatInputState,
    ChatInputStateDisplay,
    SendChatPayload,
    SendDocumentRequestsPayload,
    fromDocRequest,
} from "../../../schemas/chat";
import DocumentRequestForm from "../ChatInput/document-request-form";
import {useNavigationContext} from "../../../contexts/NavContext/nav-context";
import {useJobContext} from "../../../contexts/job_context";
import {TAG_UPDATE_COMPLETED} from "../../../events";
import {useTagContext} from "../../../contexts/tag_context";
import JobProgressList from "../../Jobs";
import ChatContentRenderer from "./chat-content-render";
import {ActionStatus, ChatMessageRead, MessageStatus} from "../../../schemas/chat_message";

import {TextColors, ThemeColors, ThemeColorsBorder} from "../../../colors";
import {PillToggle} from "../../SharedComponents/PillToggle";

const TypingDots = () => (
    <span className="inline-flex items-center gap-1" aria-label="Assistant is typing">
    <style>{`
      @keyframes dotFade { 0%,100% { opacity:.25; transform:translateY(0) } 50% { opacity:1; transform:translateY(-1px) } }
    `}</style>
    <span style={{animation: "dotFade 1.2s ease-in-out infinite"}}
          className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block"/>
    <span style={{animation: "dotFade 1.2s ease-in-out .15s infinite"}}
          className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block"/>
    <span style={{animation: "dotFade 1.2s ease-in-out .3s infinite"}}
          className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block"/>
  </span>
);

/** Show agent action progress for in-flight messages. */
const ActionProgress: React.FC<{ message: ChatMessageRead }> = ({message}) => {
    if (!message.state || message.state.status === MessageStatus.COMPLETE) return null;

    return (
        <div className="mt-2 space-y-1">
            {message.state.actions.map((action, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-gray-500">
                    {action.status === ActionStatus.RUNNING && (
                        <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"/>
                    )}
                    {action.status === ActionStatus.COMPLETE && (
                        <span className="w-2 h-2 rounded-full bg-green-400"/>
                    )}
                    {action.status === ActionStatus.ERROR && (
                        <span className="w-2 h-2 rounded-full bg-red-400"/>
                    )}
                    <span>{action.name}</span>
                    {action.result_summary && (
                        <span className="text-gray-400">&mdash; {action.result_summary}</span>
                    )}
                </div>
            ))}
        </div>
    );
};

const ChatWindow: React.FC<{ currentChatId: string }> = ({currentChatId}) => {
    const {
        messages, messagesLoading, loadMessages, sendMessage,
        getChat, currentChat, setCurrentChat, replaceChat,
    } = useAIStateContext();
    const {JobsBlocker} = useNavigationContext()!;
    const {activeJobs} = useJobContext()!;
    const {tagMap} = useTagContext()!;

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const endRef = useRef<HTMLDivElement>(null);

    // "Sticky to bottom" — track whether user is near the bottom of the chat.
    // If they are, auto-scroll on new content. If they've scrolled up, leave them alone.
    const isNearBottomRef = useRef(true);
    const prevMessageCountRef = useRef(0);

    const snapToBottom = useCallback(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, []);

    // Track scroll position to determine if user is near the bottom
    const handleScroll = useCallback(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const threshold = 80; // px from bottom
        isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    }, []);

    // Auto-scroll: on chat switch always snap, on new messages only if near bottom
    useLayoutEffect(() => {
        const prevCount = prevMessageCountRef.current;
        const newCount = messages.length;
        prevMessageCountRef.current = newCount;

        if (prevCount === 0 || newCount > prevCount || isNearBottomRef.current) {
            snapToBottom();
        }
    }, [messages, currentChatId, snapToBottom]);

    // Reset on chat switch so we always scroll on new chat
    useEffect(() => {
        prevMessageCountRef.current = 0;
        isNearBottomRef.current = true;
    }, [currentChatId]);

    // True while we're fetching chat data + messages on tab switch
    const [chatSwitchLoading, setChatSwitchLoading] = useState(false);

    // Refs so the load effect only re-runs when the actual chat ID changes,
    // not when callback references are recreated by context re-renders.
    const getChatRef = useRef(getChat);
    getChatRef.current = getChat;
    const loadMessagesRef = useRef(loadMessages);
    loadMessagesRef.current = loadMessages;

    // Load chat data + messages on mount / chat change.
    // Uses a cancellation flag to prevent stale getChat() responses from
    // overwriting the current tab when the user switches quickly.
    useEffect(() => {
        let active = true;
        setChatSwitchLoading(true);
        const load = async () => {
            try {
                const data = await getChatRef.current(currentChatId);
                if (!active) return;
                setCurrentChat(data);
            } catch (err) {
                if (!active) return;
                console.error("Error loading chat:", err);
            }
            if (!active) return;
            await loadMessagesRef.current(currentChatId);
            if (active) setChatSwitchLoading(false);
        };
        load();
        return () => { active = false; };
    }, [currentChatId]);

    // Refresh chat + messages when a tag update job completes (doc request results ready).
    // Uses a ref for currentChatId to avoid re-subscribing the listener on every chat change.
    const currentChatIdRef = useRef(currentChatId);
    currentChatIdRef.current = currentChatId;

    // Refs for the tag-update handler so dependencies stay stable (no re-subscribe churn).
    const getChatRef2 = useRef(getChat);
    getChatRef2.current = getChat;
    const loadMessagesRef2 = useRef(loadMessages);
    loadMessagesRef2.current = loadMessages;
    const replaceChatRef = useRef(replaceChat);
    replaceChatRef.current = replaceChat;
    const tagMapRef = useRef(tagMap);
    tagMapRef.current = tagMap;

    useEffect(() => {
        const onTagUpdated = async () => {
            const chatId = currentChatIdRef.current;
            if (!chatId) return;

            // Always refresh chat + messages when a tag update completes.
            // The summary message from doc request processing may be new.
            try {
                if (currentChatIdRef.current !== chatId) return;
                const fresh = await getChatRef2.current(chatId);
                if (currentChatIdRef.current !== chatId) return;
                setCurrentChat(fresh);
                replaceChatRef.current(fresh);
                await loadMessagesRef2.current(chatId);
            } catch (err) {
                console.error("Error handling tag update for chat:", err);
            }
        };
        window.addEventListener(TAG_UPDATE_COMPLETED, onTagUpdated);
        return () => window.removeEventListener(TAG_UPDATE_COMPLETED, onTagUpdated);
    }, []); // Stable — uses refs for everything

    const sendMessageRef = useRef(sendMessage);
    sendMessageRef.current = sendMessage;
    const handleSendMessage = useCallback(async (payload: SendChatPayload) => {
        const params: SendMessageParams = {
            text: payload.input,
            sentFromChatInputState: payload.inputState,
            chatContexts: payload.chatContexts,
            tagsToInfer: payload.tagsToInfer,
            documentRequest: payload.documentRequest,
        };
        await sendMessageRef.current(params);
    }, []);

    const handleSendDocumentRequestsRef = useRef(async (_payload: SendDocumentRequestsPayload) => {});
    handleSendDocumentRequestsRef.current = async (payload: SendDocumentRequestsPayload) => {
        if (!payload.tagsToInfer) return;
        const docReq = fromDocRequest(payload.documentRequest, payload.tagsToInfer) as SendDocumentRequestsPayload;
        await handleSendMessage(docReq as SendChatPayload);
    };
    const handleSendDocumentRequests = useCallback(async (payload: SendDocumentRequestsPayload) => {
        await handleSendDocumentRequestsRef.current(payload);
    }, []);

    const [currentInputState, setCurrentInputState] = useState<ChatInputState>(ChatInputState.ANY_TEXT);

    // Sync input state from chat data
    useEffect(() => {
        if (currentChat?.chat_input_state) {
            setCurrentInputState(currentChat.chat_input_state);
        } else {
            setCurrentInputState(ChatInputState.ANY_TEXT);
        }
    }, [currentChat]);

    const tagUpdateJobs = activeJobs?.filter(j => j.type === JobType.TAG_UPDATE) ?? [];
    const inputLocked = messagesLoading || tagUpdateJobs.length > 0;

    return (
        <JobsBlocker
            className={`flex flex-col h-full w-full overflow-hidden rounded-xl ${ThemeColorsBorder.neutralMid} ${ThemeColors.neutralLight} shadow-inner`}>

            {/* Message area */}
            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-5 relative"
            >
                {chatSwitchLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
                        <div className="flex items-center gap-2 text-sm text-zinc-400">
                            <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"/>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                            </svg>
                            Loading chat&hellip;
                        </div>
                    </div>
                )}
                <div className="flex flex-col justify-end min-h-full">
                    <div className="space-y-5">
                        {messages.map((msg, i) => {
                            const isUser = msg.role === "user";
                            const isAssistant = msg.role === "assistant";
                            const isPending = isAssistant && msg.state &&
                                (msg.state.status === MessageStatus.PENDING || msg.state.status === MessageStatus.PROCESSING);
                            const hasContent = msg.content && (msg.content.text || (msg.content.content_chunks && msg.content.content_chunks.length > 0));

                            return (
                                <div
                                    key={msg.id ?? `${msg.created_at}-${i}`}
                                    className={`max-w-3xl w-fit break-words px-4 py-3 rounded-2xl shadow-sm text-sm leading-relaxed ${
                                        isUser
                                            ? "bg-[#eaeafd] ml-auto text-gray-900"
                                            : "bg-white border border-gray-200 text-gray-800 mr-auto"
                                    }`}
                                >
                                    {isPending && !hasContent && (
                                        <div className="text-xs text-gray-500 flex items-center">
                                            <TypingDots/>
                                        </div>
                                    )}
                                    {isPending && <ActionProgress message={msg}/>}
                                    {hasContent ? (
                                        <ChatContentRenderer content={msg.content!}/>
                                    ) : isUser && msg.content?.text ? (
                                        <div className="prose prose-sm max-w-none">
                                            {msg.content.text}
                                        </div>
                                    ) : null}

                                    {msg.state?.status === MessageStatus.ERROR && (
                                        <div className="mt-2 text-xs text-red-500">
                                            Error: {msg.state.summary ?? "Something went wrong"}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {tagUpdateJobs.length > 0 && (
                            <JobProgressList jobs={tagUpdateJobs}/>
                        )}
                        <div ref={endRef}/>
                    </div>
                </div>
            </div>

            {/* Bottom bar */}
            <div className={`shrink-0 border-t ${ThemeColorsBorder.neutralMid} ${ThemeColors.neutralLight} px-5 py-3`}>
                <div className="flex flex-col items-end w-full mb-2">
                    <div className={`${TextColors.mid} text-xs font-medium mb-1`}>
                        {inputLocked ? "Locked" : ChatInputStateDisplay[currentInputState]}
                    </div>

                    <PillToggle
                        options={[
                            {
                                value: ChatInputState.ANY_TEXT,
                                label: ChatInputStateDisplay[ChatInputState.ANY_TEXT],
                                icon: <FiMessageSquare className="w-4 h-4"/>,
                            },
                            {
                                value: ChatInputState.DOC_REQ_IN,
                                label: ChatInputStateDisplay[ChatInputState.DOC_REQ_IN],
                                icon: <FiFileText className="w-4 h-4"/>,
                            },
                        ]}
                        value={currentInputState}
                        onChange={setCurrentInputState}
                        disabled={inputLocked}
                        lockedIcon={<FiLock className="w-4 h-4"/>}
                        size="md"
                    />
                </div>

                <div>
                    {currentChat && currentInputState === ChatInputState.DOC_REQ_IN ? (
                        <DocumentRequestForm currentChat={currentChat} sendDocumentRequests={handleSendDocumentRequests}/>
                    ) : currentChat ? (
                        <ChatInput currentChat={currentChat} sendMessage={handleSendMessage} disabled={inputLocked}/>
                    ) : null}
                </div>
            </div>
        </JobsBlocker>
    );
};

export default ChatWindow;
